import * as Yup from 'yup';
import { TFunction } from 'i18next';

/**
 * Field-level Yup builders for JobPosition fields.
 *
 * Single source of truth for per-field validation rules. Compose these into
 * step-specific schemas (job-position-onboarding) or any other JP-creating
 * flow (autonomous org onboarding) to keep constraints in sync — when the
 * backend changes a rule, only one place needs updating.
 *
 * Cross-field tests (e.g. salary min/max sanity, accommodation conditional
 * on seasonal flags) stay in the consuming step schema, since they describe
 * step-level composition, not field-level constraints.
 */

export const staffRequestedField = (t: TFunction) =>
  Yup.number()
    .required(t('errors.required_field'))
    .typeError(t('steps.other_requirements.staff_requested.validation_numeric'))
    .positive(t('steps.other_requirements.staff_requested.validation_positive'))
    .integer(t('steps.other_requirements.staff_requested.validation_integer'))
    .max(999, t('steps.other_requirements.staff_requested.validation_max'));

export const advertisedLocationField = (t: TFunction) => Yup.string().required(t('errors.required_field'));

/**
 * Base validator for the worker_max_distance field (radius in km from the venue
 * within which a candidate may apply). The "required" rule is intentionally
 * NOT applied here because requirement depends on `advertised_location`. Step
 * schemas compose `.when('advertised_location', ...)` on top of this builder.
 */
export const workerMaxDistanceField = (t: TFunction) =>
  Yup.number()
    .nullable()
    .typeError(t('errors.required_field'))
    .positive(t('errors.required_field'))
    .integer(t('errors.required_field'));

export const positiveSalaryField = (t: TFunction) =>
  Yup.number()
    .nullable()
    .typeError(t('steps.salary.salary_validation_numeric'))
    .positive(t('steps.salary.salary_validation_positive'));

export const restaurantIdField = (t: TFunction) => Yup.string().required(t('errors.venue_required'));

export const domainInterestField = (t: TFunction) => Yup.string().required(t('errors.domain_interest_required'));

/**
 * Subset of JP fields that any JP-creating flow MUST satisfy before sending
 * the payload to `POST /organizations` or `POST /job_positions`.
 *
 * Use this for last-mile defensive validation in flows where the user may
 * skip individual form steps (e.g. autonomous org onboarding with AI-prefill).
 */
export const jobPositionSubmitGuardSchema = (t: TFunction) =>
  Yup.object({
    staff_requested: staffRequestedField(t),
  });
